(() => {
  const SEARCH_PATH = '/search';
  const SUGGEST_PATH = '/search/suggest';
  if (window.__titleArtistSearchInstalled) return;
  window.__titleArtistSearchInstalled = true;

  const artistIndexElement = document.getElementById('search-artist-index');
  let artists = [];
  try {
    artists = JSON.parse(artistIndexElement?.textContent || '[]');
  } catch (_error) {
    // Keep title and tag search available if the optional index is missing.
  }

  const normalizeArtistName = (value) =>
    normalizeSearchTerm(value).normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

  const FIELD_QUERY_PATTERN = /(^|\s)(title|tag|vendor|product_type|variants\.(sku|barcode|title)|body):/i;

  const normalizeSearchTerm = (value) =>
    String(value || '')
      .replace(/[\u0000-\u001f\u007f]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const quoteSearchValue = (value) => `"${String(value).replace(/["\\]/g, ' ').replace(/\s+/g, ' ').trim()}"`;

  const getSearchTokens = (term) => term.match(/[\p{L}\p{N}][\p{L}\p{N}'&/-]*/gu) || [];

  const buildRestrictedQuery = (term, predictive = false) => {
    const cleanTerm = normalizeSearchTerm(term);

    if (!cleanTerm || FIELD_QUERY_PATTERN.test(cleanTerm)) return cleanTerm;

    const titleTerms = getSearchTokens(cleanTerm)
      .map((token) => `title:${quoteSearchValue(token)}`)
      .join(' ');

    const normalizedTerm = normalizeArtistName(cleanTerm);
    const tags = new Set([cleanTerm]);

    // Artist names are displayed with spaces, but product tags use handles.
    const handleTerm = normalizedTerm.replace(/ /g, '-');
    if (handleTerm) tags.add(handleTerm);
    if (normalizedTerm) {
      for (const artist of artists) {
        if (typeof artist.name === 'string' && typeof artist.tag === 'string' &&
            (normalizeArtistName(artist.name).includes(normalizedTerm) ||
             normalizeArtistName(artist.tag).includes(normalizedTerm))) {
          tags.add(artist.tag);
        }
      }
    }

    const tagTerms = Array.from(tags, (tag) => `tag:${quoteSearchValue(tag)}`);
    // Predictive field-qualified title queries do not preserve native prefix matching.
    const titleQuery = predictive ? getSearchTokens(cleanTerm).join(' ') : titleTerms;
    return [titleQuery, ...tagTerms].filter(Boolean).join(' OR ');
  };

  const isStorefrontSearchUrl = (url, path) => {
    try {
      const parsedUrl = new URL(url, window.location.origin);
      return parsedUrl.origin === window.location.origin && (parsedUrl.pathname === path || parsedUrl.pathname.endsWith(path) ||
        (path === SUGGEST_PATH && parsedUrl.pathname.endsWith(`${path}.json`)));
    } catch (_error) {
      return false;
    }
  };

  const getRestrictedUrl = (url) => {
    const parsedUrl = new URL(url, window.location.origin);
    const originalQuery = parsedUrl.searchParams.get('q');
    const restrictedQuery = buildRestrictedQuery(originalQuery, isStorefrontSearchUrl(url, SUGGEST_PATH));

    if (restrictedQuery && restrictedQuery !== originalQuery) {
      parsedUrl.searchParams.set('q', restrictedQuery);
    }

    if (parsedUrl.pathname === SEARCH_PATH && !parsedUrl.searchParams.has('type')) {
      parsedUrl.searchParams.set('type', 'product');
    }

    return parsedUrl;
  };

  const originalFetch = window.fetch;

  window.fetch = (resource, options) => {
    if (typeof resource === 'string' || resource instanceof URL) {
      const resourceUrl = resource.toString();

      if (isStorefrontSearchUrl(resourceUrl, SUGGEST_PATH)) {
        return originalFetch(getRestrictedUrl(resourceUrl).toString(), options);
      }

      return originalFetch(resource, options);
    }

    if (resource instanceof Request && isStorefrontSearchUrl(resource.url, SUGGEST_PATH)) {
      const method = resource.method.toUpperCase();
      const requestInit = {
        method: resource.method,
        headers: resource.headers,
        body: method === 'GET' || method === 'HEAD' ? undefined : resource.clone().body,
        mode: resource.mode,
        credentials: resource.credentials,
        cache: resource.cache,
        redirect: resource.redirect,
        referrer: resource.referrer,
        referrerPolicy: resource.referrerPolicy,
        integrity: resource.integrity,
        keepalive: resource.keepalive,
        signal: resource.signal,
      };

      return originalFetch(new Request(getRestrictedUrl(resource.url).toString(), requestInit), options);
    }

    return originalFetch(resource, options);
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement) || !isStorefrontSearchUrl(form.action, SEARCH_PATH)) return;

      const searchInput = form.querySelector('input[name="q"]');
      if (!(searchInput instanceof HTMLInputElement)) return;

      const restrictedQuery = buildRestrictedQuery(searchInput.value);
      if (!restrictedQuery || restrictedQuery === searchInput.value) return;

      event.preventDefault();

      const searchUrl = new URL(form.action, window.location.origin);
      searchUrl.searchParams.set('q', restrictedQuery);
      searchUrl.searchParams.set('type', 'product');

      for (const element of Array.from(form.elements)) {
        if (
          !(element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement) ||
          !element.name ||
          element.name === 'q' ||
          element.disabled
        ) {
          continue;
        }

        if ((element instanceof HTMLInputElement) && ['checkbox', 'radio'].includes(element.type) && !element.checked) continue;

        searchUrl.searchParams.append(element.name, element.value);
      }

      window.location.href = searchUrl.toString();
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter') return;

      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.name !== 'q') return;

      const form = input.form;
      if (!form || !isStorefrontSearchUrl(form.action, SEARCH_PATH)) return;

      const searchComponent = input.closest('predictive-search-component');
      const selectedResult = searchComponent?.querySelector('[aria-selected="true"]');
      if (selectedResult) return;

      const restrictedQuery = buildRestrictedQuery(input.value);
      if (!restrictedQuery || restrictedQuery === input.value) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const searchUrl = new URL(form.action, window.location.origin);
      searchUrl.searchParams.set('q', restrictedQuery);
      searchUrl.searchParams.set('type', 'product');
      window.location.href = searchUrl.toString();
    },
    true
  );
})();
